import React, { memo, useCallback } from "react";
import { connect } from "react-redux";

import { CardElement } from "@stripe/react-stripe-js";
import { Token } from "@stripe/stripe-js";
import { useIntl } from "react-intl";

import Plan from "@mapmycustomers/shared/types/Plan";
import Tier from "@mapmycustomers/shared/types/plans/Tier";
import { Modal } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import configService from "@app/config/ConfigService";
import localSettings from "@app/config/LocalSettings";
import authService from "@app/store/auth/AuthService";
import { RootState } from "@app/store/rootReducer";
import { getLegacyAppUrl } from "@app/util/appUrl";

import { getLicenses, getPriceInfo, isSuccessState, isUpgradeLoading } from "../../store";
import { resetSuccessState, upgrade } from "../../store/actions";
import { PriceInfo } from "../../util/price";

import styles from "./CheckoutModal.module.scss";
import { CARD_OPTIONS } from "./const";
import Footer from "./Footer";
import useCardInput from "./useCardInput";

interface Props {
  licenses: number;
  loading: boolean;
  onCompleteCheckout?: () => void;
  onHide?: () => void;
  priceInfo: PriceInfo;
  resetSuccessState?: () => void;
  selectedTier: Tier;
  success: boolean;
  upgrade: (payload: { licenses: number; planId: Plan["id"]; token: Token }) => void;
}

const CheckoutModal: React.FC<Props> = ({
  licenses,
  loading,
  onCompleteCheckout,
  onHide,
  priceInfo,
  resetSuccessState,
  selectedTier,
  success,
  upgrade,
}) => {
  const intl = useIntl();

  const handleClose = useCallback(() => {
    resetSuccessState?.();
    onHide?.();
  }, [onHide, resetSuccessState]);

  const handleCloseSuccessfully = useCallback(() => {
    handleClose();
    onCompleteCheckout?.();
  }, [handleClose, onCompleteCheckout]);

  const { error, getToken, loadingToken } = useCardInput();
  const handlePayClick = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      return;
    }
    upgrade({ licenses, planId: selectedTier.id, token });
  }, [getToken, licenses, selectedTier, upgrade]);

  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();
  if (!token) {
    return null;
  }

  return (
    <Modal
      className={styles.container}
      footer={
        <Footer
          loading={loadingToken || loading}
          onCloseClick={handleCloseSuccessfully}
          onPayClick={handlePayClick}
          price={priceInfo.dueTodayPrice}
          success={success}
        />
      }
      onCancel={handleClose}
      title={
        success
          ? intl.formatMessage({
              id: "billing.checkout.success.title",
              defaultMessage: "Success",
              description: "Title of a successful Checkout modal on Billing page",
            })
          : intl.formatMessage({
              id: "billing.checkout.title",
              defaultMessage: "Checkout",
              description: "Title of Checkout modal on Billing page",
            })
      }
      visible
    >
      {success ? (
        <span>
          {intl.formatMessage(
            {
              id: "billing.checkout.successMessage",
              defaultMessage:
                "Your purchase has been successfully completed! <link>Manage Users</link>",
              description: "Checked out successfully message in Checkout modal on Billing page",
            },
            {
              link: (text: string) =>
                localSettings.wasOpenedFromMobileApp() ? null : (
                  <ButtonLink href={getLegacyAppUrl(baseOldAppUrl, token, "/dashboard/teams")}>
                    {text}
                  </ButtonLink>
                ),
            }
          )}
        </span>
      ) : (
        <>
          <fieldset className={styles.cardInputContainer}>
            <CardElement options={CARD_OPTIONS} />
          </fieldset>
          {error && <div className={styles.error}>{error}</div>}
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  licenses: getLicenses(state),
  loading: isUpgradeLoading(state),
  priceInfo: getPriceInfo(state),
  success: isSuccessState(state),
});

const mapDispatchToProps = {
  resetSuccessState,
  upgrade: upgrade.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(CheckoutModal));
